import { useRef, useState, useCallback, useEffect } from 'react';
import { Button } from '../ui';

/**
 * Fix EXIF orientation by drawing image through a canvas.
 * Modern browsers auto-apply EXIF rotation when drawing to canvas.
 */
async function fixImageOrientation(base64Image: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Create canvas with image dimensions
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(base64Image); // Fallback to original
        return;
      }

      // Draw image - browser will auto-apply EXIF orientation
      ctx.drawImage(img, 0, 0);

      // Convert back to base64
      const fixedBase64 = canvas.toDataURL('image/jpeg', 0.9);
      resolve(fixedBase64);
    };
    img.onerror = () => {
      resolve(base64Image); // Fallback to original on error
    };
    img.src = base64Image;
  });
}

interface CameraCaptureProps {
  onCapture: (imageData: string) => void;
  onCancel?: () => void;
  onNext?: () => void;
  onBack?: () => void;
  title?: string;
  instructions?: string;
  autoRestart?: boolean;
  fullScreen?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  captureCount?: number;
  processingCount?: number;
  statusText?: string;
  aspectRatio?: 'landscape' | 'portrait' | 'document' | 'native';
  isProcessing?: boolean;
}

export function CameraCapture({
  onCapture,
  onCancel,
  onNext,
  onBack,
  title = 'Take Photo',
  instructions = 'Select an option to capture or choose an image',
  autoRestart = false,
  fullScreen = false,
  nextButtonText = 'Done',
  backButtonText = 'Back',
  captureCount = 0,
  processingCount = 0,
  statusText,
  aspectRatio = 'landscape',
  isProcessing = false
}: CameraCaptureProps) {
  // File input refs for different capture modes
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [webcamError, setWebcamError] = useState<string | null>(null);

  // Detect mobile on mount
  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  // Screen capture for screenshots (desktop only)
  const handleScreenCapture = useCallback(async () => {
    try {
      // Request screen/window selection
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'window' } as MediaTrackConstraints,
        audio: false
      });

      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;

      // Wait for video to load
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      // Small delay to ensure frame is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture frame to canvas
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/png');

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        if (autoRestart) {
          onCapture(imageData);
        } else {
          setCapturedImage(imageData);
        }
      }
    } catch (err) {
      console.error('Screen capture error:', err);
      // User cancelled or denied permission - no need to show error
    }
  }, [autoRestart, onCapture]);

  const aspectClass = aspectRatio === 'native'
    ? ''
    : aspectRatio === 'portrait'
      ? 'aspect-[9/16]'
      : aspectRatio === 'document'
        ? 'aspect-[3/4]'
        : 'aspect-[4/3]';

  // Handler for camera input - always show preview unless autoRestart is true
  const handleCameraCapture = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const rawImageData = e.target?.result as string;
        // Fix EXIF orientation before using
        const imageData = await fixImageOrientation(rawImageData);
        if (autoRestart) {
          onCapture(imageData);
        } else {
          setCapturedImage(imageData);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  }, [autoRestart, onCapture]);

  // Handler for library/file input - always show preview
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const rawImageData = e.target?.result as string;
        // Fix EXIF orientation before using
        const imageData = await fixImageOrientation(rawImageData);
        if (autoRestart) {
          onCapture(imageData);
        } else {
          setCapturedImage(imageData);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  }, [autoRestart, onCapture]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage);
    }
  }, [capturedImage, onCapture]);

  const handleCancel = useCallback(() => {
    setCapturedImage(null);
    onCancel?.();
  }, [onCancel]);

  // Webcam functions for desktop
  const startWebcam = useCallback(async () => {
    setWebcamError(null);
    setShowWebcam(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsStreaming(true);
        };
      }
    } catch (err) {
      console.error('Webcam error:', err);
      setWebcamError('Could not access camera. Please use file upload instead.');
      setIsStreaming(false);
    }
  }, []);

  const stopWebcam = useCallback(() => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
    setShowWebcam(false);
    setWebcamError(null);
  }, []);

  const captureFromWebcam = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg', 0.85);
        stopWebcam();
        if (autoRestart) {
          onCapture(imageData);
        } else {
          setCapturedImage(imageData);
        }
      }
    }
  }, [stopWebcam, autoRestart, onCapture]);

  // Cleanup webcam on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleTakePhoto = useCallback(() => {
    if (isMobile) {
      // On mobile, use the file input with capture attribute
      cameraInputRef.current?.click();
    } else {
      // On desktop, open webcam
      startWebcam();
    }
  }, [isMobile, startWebcam]);

  // Hidden file inputs
  const cameraInput = (
    <input
      ref={cameraInputRef}
      type="file"
      accept="image/*"
      capture="environment"
      onChange={handleCameraCapture}
      className="hidden"
    />
  );

  const libraryInput = (
    <input
      ref={libraryInputRef}
      type="file"
      accept="image/*"
      onChange={handleFileUpload}
      className="hidden"
    />
  );

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*,.pdf"
      onChange={handleFileUpload}
      className="hidden"
    />
  );

  // Webcam modal for desktop
  if (showWebcam) {
    return (
      <div className="flex flex-col items-center gap-4 w-full">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>

        <div className="relative w-full max-w-lg aspect-video bg-gray-900 rounded-xl overflow-hidden shadow-lg">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${isStreaming ? 'block' : 'hidden'}`}
          />
          {!isStreaming && !webcamError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
              <p className="text-white/70 text-sm">Starting camera...</p>
            </div>
          )}
          {webcamError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-4">
              <p className="text-red-400 text-sm text-center">{webcamError}</p>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="flex gap-3 w-full max-w-sm">
          <Button variant="outline" onClick={stopWebcam} className="flex-1">
            Cancel
          </Button>
          {isStreaming && (
            <Button onClick={captureFromWebcam} className="flex-1">
              Capture
            </Button>
          )}
          {webcamError && (
            <Button onClick={() => libraryInputRef.current?.click()} className="flex-1">
              Choose File
            </Button>
          )}
        </div>
        {libraryInput}
      </div>
    );
  }

  // Preview mode for captured image
  if (capturedImage && !autoRestart) {
    return (
      <div className="flex flex-col items-center gap-4 w-full">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <div className="relative w-full max-w-4xl bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
          <img
            src={capturedImage}
            alt="Captured"
            className="w-full h-auto max-h-[70vh] object-contain"
          />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          Review your image. Make sure the text is clear and readable.
        </p>
        <div className="flex gap-3 w-full max-w-sm">
          <Button variant="outline" onClick={handleRetake} className="flex-1" disabled={isProcessing}>
            Retake
          </Button>
          <Button onClick={handleConfirm} className="flex-1" disabled={isProcessing}>
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </span>
            ) : (
              'Use Photo'
            )}
          </Button>
        </div>
        {cameraInput}
        {libraryInput}
        {fileInput}
      </div>
    );
  }

  // Full-screen mode with button interface
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col z-50">
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">{title}</h3>
              <p className="text-white/70 text-sm">{instructions}</p>
            </div>
            {onCancel && (
              <button onClick={handleCancel} className="text-white/80 hover:text-white p-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            {captureCount > 0 && (
              <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">
                {captureCount} captured
              </span>
            )}
            {processingCount > 0 && (
              <span className="bg-yellow-500/80 text-white text-sm px-3 py-1 rounded-full animate-pulse">
                {processingCount} processing
              </span>
            )}
            {statusText && (
              <span className="bg-green-500/80 text-white text-sm px-3 py-1 rounded-full">
                {statusText}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="flex flex-col items-center gap-6 w-full max-w-sm">
            {/* Camera icon placeholder */}
            <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center">
              <svg className="w-16 h-16 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>

            {/* Capture buttons */}
            <div className="flex flex-col gap-3 w-full">
              <Button
                onClick={handleTakePhoto}
                size="lg"
                className="w-full"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Take Photo
              </Button>

              <Button
                variant="outline"
                onClick={() => libraryInputRef.current?.click()}
                size="lg"
                className="w-full bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Photo Library
              </Button>

              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                size="lg"
                className="w-full bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Choose File
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <div className="w-20">
              {onBack && (
                <button onClick={onBack} className="text-white/80 hover:text-white text-sm py-2 px-3">
                  {backButtonText}
                </button>
              )}
            </div>
            <div className="w-20" />
            <div className="w-20">
              {onNext && captureCount > 0 && (
                <button onClick={onNext} className="bg-primary-600 text-white text-sm py-2 px-3 rounded-lg font-medium">
                  {nextButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
        {cameraInput}
        {libraryInput}
        {fileInput}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // Standard inline mode with button interface
  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center">{instructions}</p>

      {/* Camera icon placeholder */}
      <div className="w-full max-w-lg bg-gray-100 dark:bg-gray-800 rounded-xl shadow-lg flex items-center justify-center py-6 px-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Use the buttons below to capture or select an image
          </p>
        </div>
      </div>

      {cameraInput}
      {libraryInput}
      {fileInput}
      <canvas ref={canvasRef} className="hidden" />

      {/* Capture buttons */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        {/* Screen Capture - desktop only */}
        {!isMobile && (
          <Button
            onClick={handleScreenCapture}
            size="lg"
            className="w-full"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} fill="none" />
            </svg>
            Screenshot
          </Button>
        )}

        <Button
          onClick={handleTakePhoto}
          size="lg"
          className="w-full"
          variant={isMobile ? undefined : 'outline'}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Take Photo
        </Button>

        <Button
          variant="outline"
          onClick={() => libraryInputRef.current?.click()}
          size="lg"
          className="w-full"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Photo Library
        </Button>

        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          size="lg"
          className="w-full"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Choose File
        </Button>
      </div>

      {onCancel && (
        <Button variant="outline" onClick={handleCancel} className="w-full max-w-sm mt-2" size="sm">
          Cancel
        </Button>
      )}
    </div>
  );
}
